import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ROLE_INFO = {
  owner:  { label:'Owner',  color:'var(--accent2)', desc:'Full access. Can invite and manage everyone.' },
  editor: { label:'Editor', color:'var(--blue)',  desc:'Can view reports, AI chat, manage assessments and respondents.' },
  viewer: { label:'Viewer', color:'var(--text2)',   desc:'Can view reports and AI chat. Read-only.' },
};

export default function Collaborators({ showToast }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pageData = location.state || {};
  const { authFetch, user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selCompany, setSelCompany] = useState(pageData?.companyId || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);

  useEffect(() => { authFetch('/api/companies').then(r=>r.json()).then(d=>setCompanies(Array.isArray(d)?d:[])); }, [authFetch]);
  useEffect(() => { if (selCompany) load(); }, [selCompany]);

  const load = async () => {
    setLoading(true);
    const res = await authFetch(`/api/collaborators/${selCompany}`);
    const d = await res.json();
    if (res.ok) setData(d);
    else showToast(d.error || 'Failed', 'error');
    setLoading(false);
  };

  const invite = async e => {
    e.preventDefault();
    if (!inviteEmail.includes('@')) { showToast('Enter a valid email', 'error'); return; }
    setInviting(true);
    const res = await authFetch(`/api/collaborators/${selCompany}/invite`, {
      method: 'POST', body: JSON.stringify({ email: inviteEmail, role: inviteRole })
    });
    const d = await res.json();
    if (res.ok) { showToast(`Invite sent to ${inviteEmail}`); setInviteEmail(''); load(); }
    else if (d.upgrade) { showToast(d.error, 'error'); navigate('/billing'); }
    else showToast(d.error || 'Failed', 'error');
    setInviting(false);
  };

  const updateRole = async (collabId, role) => {
    await authFetch(`/api/collaborators/${selCompany}/${collabId}`, { method: 'PUT', body: JSON.stringify({ role }) });
    showToast('Role updated'); load();
  };

  const revoke = async (collabId, email) => {
    if (!window.confirm(`Revoke access for ${email}?`)) return;
    const res = await authFetch(`/api/collaborators/${selCompany}/${collabId}`, { method: 'DELETE' });
    if (res.ok) { showToast('Access revoked'); load(); }
    else showToast('Failed', 'error');
  };

  const resend = async (collabId, email) => {
    const res = await authFetch(`/api/collaborators/${selCompany}/${collabId}/resend`, { method: 'POST' });
    if (res.ok) showToast(`Invite resent to ${email}`);
    else showToast('Resend failed', 'error');
  };

  const statusBadge = s => ({
    pending:  { color:'var(--blue)',  label:'Pending' },
    accepted: { color:'var(--green)', label:'Active' },
    revoked:  { color:'var(--text3)', label:'Revoked' },
  }[s] || { color:'var(--text3)', label:s });

  if (user?.plan === 'free') return (
    <div>
      <div className="page-header"><div className="page-title">Team Collaborators</div></div>
      <div className="card" style={{ textAlign:'center', padding:56, borderColor:'rgba(201,151,58,0.2)' }}>
        <div style={{ fontSize:40, marginBottom:16 }}>👥</div>
        <div style={{ fontFamily:'var(--ff-d)', fontSize:20, fontWeight:800, marginBottom:8 }}>Pro Feature</div>
        <div style={{ color:'var(--text2)', fontSize:13, marginBottom:24, maxWidth:400, margin:'0 auto 24px' }}>
          Invite teammates as Viewers or Editors on any company workspace. They get their own login with scoped access to reports, AI, and assessments.
        </div>
        <button className="btn btn-primary" style={{ padding:'11px 28px' }} onClick={()=>navigate('/billing')}>Upgrade to Pro →</button>
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Team Collaborators</div>
        <div className="page-sub">Invite teammates to view reports, use AI, and manage assessments on a company workspace</div>
      </div>

      <div className="card" style={{ marginBottom:20 }}>
        <label className="form-label">Company Workspace</label>
        <select className="form-select" style={{ maxWidth:320 }} value={selCompany} onChange={e=>setSelCompany(e.target.value)}>
          <option value="">Select company...</option>
          {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {selCompany && (
        <>
          {/* Role legend */}
          <div className="g3" style={{ marginBottom:20 }}>
            {Object.entries(ROLE_INFO).map(([role, info])=>(
              <div key={role} className="card" style={{ padding:'12px 16px' }}>
                <div style={{ fontFamily:'var(--ff-m)', fontSize:11, color:info.color, textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>{info.label}</div>
                <div style={{ fontSize:12, color:'var(--text2)', lineHeight:1.5 }}>{info.desc}</div>
              </div>
            ))}
          </div>

          {/* Current team */}
          <div className="sec-header"><div className="sec-title">Current Team</div><div className="sec-line"></div></div>
          {loading ? (
            <div style={{ textAlign:'center', padding:32 }}><div className="loader" style={{ margin:'0 auto' }}></div></div>
          ) : (
            <div className="card" style={{ marginBottom:24, padding:0, overflow:'hidden' }}>
              <table className="table">
                <thead><tr><th>Person</th><th>Role</th><th>Status</th><th>Joined</th><th>Actions</th></tr></thead>
                <tbody>
                  {/* Owner row */}
                  {data?.owner && (
                    <tr>
                      <td>
                        <div style={{ fontWeight:600, fontSize:13 }}>{data.owner.name} <span style={{ fontSize:10, color:'var(--text3)' }}>(you)</span></div>
                        <div style={{ fontSize:11, color:'var(--text3)', fontFamily:'var(--ff-m)' }}>{data.owner.email}</div>
                      </td>
                      <td><span className="tag" style={{ fontSize:10, color:ROLE_INFO.owner.color, borderColor:`${ROLE_INFO.owner.color}40` }}>Owner</span></td>
                      <td><span className="tag priority-strong" style={{ fontSize:10 }}>Active</span></td>
                      <td style={{ fontSize:11, color:'var(--text3)' }}>—</td>
                      <td style={{ color:'var(--text3)', fontSize:12 }}>Workspace owner</td>
                    </tr>
                  )}
                  {(data?.collaborators || []).filter(c=>c.status !== 'revoked').map(c=>(
                    <tr key={c.id}>
                      <td>
                        <div style={{ fontWeight:600, fontSize:13 }}>{c.name || '—'}</div>
                        <div style={{ fontSize:11, color:'var(--text3)', fontFamily:'var(--ff-m)' }}>{c.email}</div>
                      </td>
                      <td>
                        <select className="form-select" style={{ padding:'3px 8px', fontSize:11, width:'auto', minWidth:80 }}
                          value={c.role} onChange={e=>updateRole(c.id, e.target.value)}>
                          <option value="viewer">Viewer</option>
                          <option value="editor">Editor</option>
                        </select>
                      </td>
                      <td>
                        <span className="tag" style={{ fontSize:10, color:statusBadge(c.status).color, borderColor:`${statusBadge(c.status).color}40` }}>
                          {statusBadge(c.status).label}
                        </span>
                      </td>
                      <td style={{ fontSize:11, color:'var(--text3)' }}>
                        {c.accepted_at ? new Date(c.accepted_at).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          {c.status === 'pending' && (
                            <button className="btn btn-ghost btn-sm" style={{ fontSize:10 }} onClick={()=>resend(c.id,c.email)}>Resend</button>
                          )}
                          <button className="btn btn-danger btn-sm" style={{ fontSize:10 }} onClick={()=>revoke(c.id,c.email)}>Revoke</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(data?.collaborators || []).filter(c=>c.status!=='revoked').length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign:'center', padding:'24px', color:'var(--text3)', fontSize:13 }}>
                      No collaborators yet. Invite your team below.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Invite form */}
          <div className="sec-header"><div className="sec-title">Invite Someone</div><div className="sec-line"></div></div>
          <div className="card" style={{ maxWidth:500 }}>
            <form onSubmit={invite}>
              <div className="g2" style={{ gap:12, marginBottom:14 }}>
                <div className="form-group" style={{ marginBottom:0 }}>
                  <label className="form-label">Email Address</label>
                  <input className="form-input" type="email" placeholder="colleague@company.com"
                    value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} required />
                </div>
                <div className="form-group" style={{ marginBottom:0 }}>
                  <label className="form-label">Role</label>
                  <select className="form-select" value={inviteRole} onChange={e=>setInviteRole(e.target.value)}>
                    <option value="viewer">Viewer — read reports + AI</option>
                    <option value="editor">Editor — manage assessments too</option>
                  </select>
                </div>
              </div>
              <div style={{ padding:'10px 14px', background:'rgba(201,151,58,0.04)', borderRadius:8, border:'1px solid rgba(0,212,255,0.12)', marginBottom:14 }}>
                <div style={{ fontSize:12, color:'var(--text2)' }}>
                  They'll receive an email with a link to create an account (or log in) and access this workspace.
                  In <strong style={{ color:'var(--blue)' }}>mock mode</strong>, the invite link is logged to the server console.
                </div>
              </div>
              <button className="btn btn-primary" type="submit" disabled={inviting}>
                {inviting ? 'Sending…' : '📧 Send Invite'}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
